import { reaction } from 'mobx';
import { useEffect, useState } from 'react';
import { colorSoundConfigStore } from '../../configurable-colors-sounds/color-sound-config-store';
import { getMapIconTheme } from '../helpers/map-icon-theme';
import type { MapIconTheme } from '../helpers/map-icon-theme';
import type { Theme } from '../../theme/theme-store';

/**
 * `getMapIconTheme`, for a hook that builds map layers: it re-renders the caller when the color
 * and sound config turns on or off, which can happen after the map mounts, so the caller can
 * restyle its layers instead of keeping the icons it started with. The themes are stable objects,
 * so a changed result means the icons changed.
 */
export function useMapIconTheme(colorMode: Theme): MapIconTheme {
    // Only here to re-render when the config flips; the theme is read from the store itself.
    const [, setIsEnabled] = useState(() => colorSoundConfigStore.isEnabled);

    useEffect(function followColorSoundConfig() {
        // `fireImmediately` re-reads the store as the subscription starts, so a flip between the
        // first render and this effect isn't missed.
        return reaction(() => colorSoundConfigStore.isEnabled, setIsEnabled, {
            fireImmediately: true,
        });
    }, []);

    return getMapIconTheme(colorMode);
}
